import classNames from "classnames";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

type Props = {
  children: ReactNode;
  content: ReactNode;
  title?: ReactNode;
  isDisabled?: boolean;
  width?: number;
  className?: string;
  previewClassName?: string;
  titleClassName?: string;
  contentClassName?: string;
};

const DEFAULT_PREVIEW_WIDTH = 225;
const PREVIEW_VIEWPORT_GAP = 8;
const PREVIEW_TRIGGER_GAP = 4;
const CLOSE_DELAY_MS = 120;

export const HoverListPreview: React.FC<Props> = ({
  children,
  content,
  title,
  isDisabled,
  width = DEFAULT_PREVIEW_WIDTH,
  className,
  previewClassName,
  titleClassName,
  contentClassName,
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewPosition, setPreviewPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const closePreviewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const shouldShowPreview = isPreviewOpen && !isDisabled;

  const updatePreviewPosition = useCallback(() => {
    const triggerElement = triggerRef.current;
    if (!triggerElement) return;

    const triggerRect = triggerElement.getBoundingClientRect();
    const previewHeight = previewRef.current?.offsetHeight || 0;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const preferredLeft = triggerRect.right - width;
    const left = Math.min(
      Math.max(PREVIEW_VIEWPORT_GAP, preferredLeft),
      viewportWidth - width - PREVIEW_VIEWPORT_GAP,
    );
    const preferredTop = triggerRect.bottom + PREVIEW_TRIGGER_GAP;
    const top =
      previewHeight > 0 &&
      preferredTop + previewHeight > viewportHeight - PREVIEW_VIEWPORT_GAP
        ? Math.max(
            PREVIEW_VIEWPORT_GAP,
            triggerRect.top - previewHeight - PREVIEW_TRIGGER_GAP,
          )
        : preferredTop;

    setPreviewPosition((currentPosition) => {
      if (currentPosition?.left === left && currentPosition?.top === top) {
        return currentPosition;
      }

      return { left, top };
    });
  }, [width]);

  const openPreview = () => {
    if (isDisabled) return;

    if (closePreviewTimeoutRef.current) {
      clearTimeout(closePreviewTimeoutRef.current);
      closePreviewTimeoutRef.current = null;
    }

    setIsPreviewOpen(true);
  };

  const closePreview = () => {
    closePreviewTimeoutRef.current = setTimeout(() => {
      setIsPreviewOpen(false);
      closePreviewTimeoutRef.current = null;
    }, CLOSE_DELAY_MS);
  };

  useLayoutEffect(() => {
    if (!shouldShowPreview) {
      setPreviewPosition(null);
      return;
    }

    updatePreviewPosition();
  }, [shouldShowPreview, updatePreviewPosition]);

  useLayoutEffect(() => {
    if (!shouldShowPreview || !previewPosition) return;

    updatePreviewPosition();
  }, [shouldShowPreview, previewPosition, updatePreviewPosition]);

  useEffect(() => {
    if (!shouldShowPreview) return;

    window.addEventListener("resize", updatePreviewPosition);
    window.addEventListener("scroll", updatePreviewPosition, true);

    return () => {
      window.removeEventListener("resize", updatePreviewPosition);
      window.removeEventListener("scroll", updatePreviewPosition, true);
    };
  }, [shouldShowPreview, updatePreviewPosition]);

  useEffect(() => {
    return () => {
      if (closePreviewTimeoutRef.current) {
        clearTimeout(closePreviewTimeoutRef.current);
      }
    };
  }, []);

  const preview =
    shouldShowPreview &&
    previewPosition &&
    createPortal(
      <div
        ref={previewRef}
        className={"fixed z-[1000] flex"}
        onMouseEnter={openPreview}
        onMouseLeave={closePreview}
        style={{
          left: previewPosition.left,
          top: previewPosition.top,
          width,
        }}>
        <div
          className={classNames(
            "flex max-h-[250px] w-full flex-col overflow-hidden rounded-[10px] border border-gray-300 bg-white text-[13px] shadow-lg",
            previewClassName,
          )}>
          {title && (
            <div
              className={classNames(
                "link-primary border-b border-b-primary px-4 py-2 text-[14px]",
                titleClassName,
              )}>
              {title}
            </div>
          )}

          <div className={classNames("overflow-y-auto py-2", contentClassName)}>
            {content}
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <div
      ref={triggerRef}
      className={classNames("relative inline-flex items-center", className)}
      onMouseEnter={openPreview}
      onMouseLeave={closePreview}>
      {children}
      {preview}
    </div>
  );
};
